/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/api';
import PasswordRules from '../common/PasswordRules';
import { validatePassword } from '../../utils/passwordPolicy';

// UX-005: one policy, shared with the server
const PASSWORD_REGEX = { test: (p) => validatePassword(p) === null };

export default function ResetPassword() {
  const [searchParams]          = useSearchParams();
  const token                   = searchParams.get('token') || '';
  const navigate                = useNavigate();

  const [tokenValid, setTokenValid]   = useState(null); // null=checking, true, false
  const [username, setUsername]       = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm]         = useState('');
  const [loading, setLoading]         = useState(false);
  const [done, setDone]               = useState(false);
  const [error, setError]             = useState('');
  const [showPw, setShowPw]           = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) { setTokenValid(false); return; }
    authAPI.validateResetToken(token)
      .then(res => { setTokenValid(true); setUsername(res.username || ''); })
      .catch(() => setTokenValid(false));
  }, [token]);

  const passwordStrength = (pw) => {
    if (!pw) return null;
    if (pw.length < 8)                        return { level: 'weak',   label: 'Too short',  color: '#ef4444' };
    if (!PASSWORD_REGEX.test(pw))             return { level: 'fair',   label: 'Fair — add uppercase, number & symbol', color: '#f59e0b' };
    if (pw.length >= 12 && PASSWORD_REGEX.test(pw)) return { level: 'strong', label: 'Strong',     color: '#10b981' };
    return { level: 'good', label: 'Good', color: '#3b82f6' };
  };

  const strength = passwordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    {
      const pwProblem = validatePassword(newPassword);
      if (pwProblem) return setError(pwProblem);
    }
    if (newPassword !== confirm) {
      return setError('Passwords do not match.');
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setDone(true);
    } catch (err) {
      setError(err?.error || 'Reset failed. The link may have expired — please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  // ── States ────────────────────────────────────────────────────────────────

  if (tokenValid === null) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Validating your reset link…</p>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-logo">
            <img src="/casavello-stacked.png" alt="casavello" style={{ width: "140px", height: "auto", objectFit: "contain", marginBottom: "0.5rem" }} />
            <h1>casavello</h1>
          </div>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#ef4444', marginBottom: '0.5rem' }}>Link invalid or expired</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            This password reset link has expired or already been used. Reset links are valid for 1 hour.
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="btn btn-primary"
            style={{ width: '100%', padding: '0.875rem', fontSize: '1rem', marginBottom: '0.75rem' }}
          >
            Request a new link
          </button>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-secondary"
            style={{ width: '100%', padding: '0.875rem', fontSize: '1rem' }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center' }}>
          <div className="login-logo">
            <img src="/casavello-stacked.png" alt="casavello" style={{ width: "140px", height: "auto", objectFit: "contain", marginBottom: "0.5rem" }} />
            <h1>casavello</h1>
          </div>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h2 style={{ color: 'var(--brand-green)', marginBottom: '0.5rem' }}>Password updated</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-success"
            style={{ width: '100%', padding: '0.875rem', fontSize: '1rem' }}
          >
            🔐 Go to Login
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <img src="/casavello-stacked.png" alt="casavello" style={{ width: "140px", height: "auto", objectFit: "contain", marginBottom: "0.5rem" }} />
          <h1>casavello</h1>
          <p>Your entire property portfolio, effortlessly managed</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>Choose a new password</h2>
          {username && (
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              for <strong>{username}</strong>
            </p>
          )}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Choose a strong password"
                required
                autoFocus
                autoComplete="new-password"
                style={{ paddingRight: '3rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '1rem', padding: 0 }}
                tabIndex={-1}
              >
                {showPw ? '🙈' : '👁️'}
              </button>
            </div>
            {/* Strength indicator */}
            {strength && (
              <div style={{ marginTop: '0.4rem' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                  {['weak','fair','good','strong'].map((lvl, i) => (
                    <div key={lvl} style={{
                      flex: 1, height: '4px', borderRadius: '2px',
                      background: ['weak','fair','good','strong'].indexOf(strength.level) >= i ? strength.color : 'var(--border)'
                    }} />
                  ))}
                </div>
                <small style={{ color: strength.color, fontSize: '0.75rem' }}>{strength.label}</small>
              </div>
            )}
            <PasswordRules password={newPassword} />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter your new password"
              required
              autoComplete="new-password"
            />
            {confirm && confirm !== newPassword && (
              <small style={{ color: '#ef4444', fontSize: '0.75rem' }}>Passwords don't match</small>
            )}
            {confirm && confirm === newPassword && newPassword.length >= 8 && (
              <small style={{ color: '#10b981', fontSize: '0.75rem' }}>✓ Passwords match</small>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-success"
            style={{ width: '100%', padding: '0.875rem', fontSize: '1rem', marginTop: '0.5rem' }}
            disabled={loading || !PASSWORD_REGEX.test(newPassword) || !confirm}
          >
            {loading ? 'Saving...' : '🔐 Set New Password'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', color: 'var(--brand-green)', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.875rem' }}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
